import React, { useState, useEffect } from 'react';
import { useDatabase } from '../../contexts/DatabaseContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import schema from './schema';
import validator from './validator';
import defaultData from './defaultData';

const { FiPlus, FiTrash2, FiSave, FiFileText } = FiIcons;

const ElectricalForm = ({ formInstanceId, onSave, onGeneratePDF }) => {
  const { db } = useDatabase();
  const [formData, setFormData] = useState(defaultData);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (formInstanceId) {
      loadFormData(formInstanceId);
    }
  }, [formInstanceId]);

  const loadFormData = async (instanceId) => {
    try {
      const form = await db.forms.get(parseInt(instanceId));
      if (form && form.data) {
        setFormData(form.data);
      }
    } catch (error) {
      console.error('Error loading form data:', error);
    }
  };

  const handleFieldChange = (section, field, value, index = null) => {
    setFormData(prev => {
      const newData = { ...prev };
      if (index !== null) {
        // Handle repeatable sections (like circuits)
        if (!newData[section]) newData[section] = [];
        if (!newData[section][index]) newData[section][index] = {};
        newData[section][index][field] = value;
      } else {
        // Handle regular sections
        if (!newData[section]) newData[section] = {};
        newData[section][field] = value;
      }
      return newData;
    });

    // Clear error for this field
    if (errors[field] || errors[`${section}_${field}`]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        delete newErrors[`${section}_${field}`];
        return newErrors;
      });
    }
  };

  const addCircuit = () => {
    setFormData(prev => ({
      ...prev,
      circuits: [
        ...prev.circuits,
        {
          circuitType: '',
          location: '',
          rating: '',
          protectiveDevice: '',
          testResult: '',
          remedialAction: ''
        }
      ]
    }));
  };

  const removeCircuit = (index) => {
    setFormData(prev => ({
      ...prev,
      circuits: prev.circuits.filter((_, i) => i !== index)
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const validation = validator.validate(formData);
      if (!validation.isValid) {
        setErrors(validation.errors);
        setSaving(false);
        return;
      }

      const formRecord = {
        formId: 'electrical-certificate',
        instanceId: formInstanceId || `ec-${Date.now()}`,
        data: formData,
        status: 'draft',
        updatedAt: new Date(),
        lastAccessedAt: new Date()
      };

      if (formInstanceId) {
        await db.forms.update(parseInt(formInstanceId), formRecord);
      } else {
        formRecord.createdAt = new Date();
        const id = await db.forms.add(formRecord);
        formRecord.id = id;
      }

      if (onSave) onSave(formRecord);
    } catch (error) {
      console.error('Error saving form:', error);
    }
    setSaving(false);
  };

  const renderField = (field, sectionId, value, onChange, index = null) => {
    const fieldId = index !== null ? `${sectionId}_${field.id}_${index}` : `${sectionId}_${field.id}`;
    const error = errors[fieldId] || errors[field.id];

    switch (field.type) {
      case 'text':
      case 'tel':
      case 'date':
        return (
          <div key={fieldId} className="form-group">
            <label htmlFor={fieldId} className="form-label">
              {field.label}
              {field.required && <span className="required-indicator">*</span>}
            </label>
            <input
              type={field.type}
              id={fieldId}
              className={`form-input ${error ? 'border-red-500' : ''}`}
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
              placeholder={field.placeholder}
            />
            {error && <span className="error-message">{error}</span>}
          </div>
        );
      case 'textarea':
        return (
          <div key={fieldId} className="form-group">
            <label htmlFor={fieldId} className="form-label">
              {field.label}
              {field.required && <span className="required-indicator">*</span>}
            </label>
            <textarea
              id={fieldId}
              className={`form-input ${error ? 'border-red-500' : ''}`}
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
              rows={3}
              placeholder={field.placeholder}
            />
            {error && <span className="error-message">{error}</span>}
          </div>
        );
      case 'select':
        return (
          <div key={fieldId} className="form-group">
            <label htmlFor={fieldId} className="form-label">
              {field.label}
              {field.required && <span className="required-indicator">*</span>}
            </label>
            <select
              id={fieldId}
              className={`form-input ${error ? 'border-red-500' : ''}`}
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
            >
              <option value="">Select {field.label}</option>
              {field.options?.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
            {error && <span className="error-message">{error}</span>}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="electrical-form">
      <div className="form-header mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Electrical Installation Condition Report
        </h1>
        <p className="text-gray-600">
          Complete all required fields to generate a valid electrical certificate
        </p>
      </div>

      <form onSubmit={(e) => e.preventDefault()}>
        {schema.sections.map(section => (
          <div key={section.id} className="card mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {section.title}
            </h2>
            {section.repeatable ? (
              <div>
                {formData[section.id]?.map((item, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4 mb-4 relative">
                    <div className="absolute top-2 right-2">
                      <button
                        type="button"
                        onClick={() => removeCircuit(index)}
                        className="text-red-500 hover:text-red-700 p-1"
                        disabled={formData[section.id].length === 1}
                      >
                        <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                      </button>
                    </div>
                    <h3 className="font-medium text-gray-800 mb-3">
                      Circuit {index + 1}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {section.fields.map(field =>
                        renderField(
                          field,
                          section.id,
                          item[field.id],
                          (value) => handleFieldChange(section.id, field.id, value, index),
                          index
                        )
                      )}
                    </div>
                  </div>
                ))}
                <button type="button" onClick={addCircuit} className="btn btn-secondary">
                  <SafeIcon icon={FiPlus} className="w-4 h-4" />
                  Add Circuit
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {section.fields.map(field =>
                  renderField(
                    field,
                    section.id,
                    formData[section.id]?.[field.id],
                    (value) => handleFieldChange(section.id, field.id, value)
                  )
                )}
              </div>
            )}
          </div>
        ))}

        <div className="flex gap-4 pt-6">
          <button type="button" onClick={handleSave} disabled={saving} className="btn btn-primary">
            <SafeIcon icon={FiSave} className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save Form'}
          </button>
          <button
            type="button"
            onClick={() => onGeneratePDF && onGeneratePDF(formData)}
            className="btn btn-secondary"
          >
            <SafeIcon icon={FiFileText} className="w-4 h-4" />
            Generate PDF
          </button>
        </div>
      </form>
    </div>
  );
};

export default ElectricalForm;